import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "@/components/base/Seo";
import ComicCard from "@/components/feature/ComicCard";
import { comics } from "@/mocks/comics";

export default function ComicDetail() {
  const { id } = useParams();
  const comic = comics.find((c) => c.id === id);
  const [followed, setFollowed] = useState(false);
  const [reader, setReader] = useState<{ num: string; title: string } | null>(null);

  const related = useMemo(
    () => (comic ? comics.filter((c) => c.category === comic.category && c.id !== comic.id).slice(0, 5) : []),
    [comic]
  );

  if (!comic) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-4 pt-16 text-center">
        <h1 className="font-heading text-2xl font-black text-foreground-950">未找到这部漫画</h1>
        <p className="mt-3 text-sm text-foreground-500">作品可能已下架或地址有误。</p>
        <Link to="/" className="mt-6 cursor-pointer whitespace-nowrap rounded-lg bg-primary-500 px-6 py-3 text-sm font-semibold text-background-950 hover:bg-primary-600">
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <div className="pt-16 md:pt-[72px]">
      <Seo
        title={`${comic.title}漫画 | ${comic.author} 虚空漫画网`}
        description={`${comic.title} - ${comic.description} 评分${comic.rating}，最新章节${comic.latestChapter}，虚空漫画网每日更新。`}
        path={`/comic/${comic.id}`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://talespots.github.io" },
              { "@type": "ListItem", position: 2, name: `${comic.categoryName}漫画`, item: `https://talespots.github.io/category/${comic.category}` },
              { "@type": "ListItem", position: 3, name: comic.title, item: `https://talespots.github.io/comic/${comic.id}` },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "CreativeWork",
            name: comic.title,
            author: { "@type": "Person", name: comic.author },
            genre: comic.categoryName,
            aggregateRating: { "@type": "AggregateRating", ratingValue: comic.rating, bestRating: 10, worstRating: 0, ratingCount: comic.views },
            description: comic.longDescription,
            url: `https://talespots.github.io/comic/${comic.id}`,
            image: comic.cover,
          },
        ]}
      />

      {/* 顶部信息区 */}
      <section className="relative border-b border-background-800 bg-gradient-to-b from-background-900/70 to-background-950">
        <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-12">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
            <Link to="/" className="cursor-pointer hover:text-primary-300">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to={`/category/${comic.category}`} className="cursor-pointer hover:text-primary-300">{comic.categoryName}漫画</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-300">{comic.title}</span>
          </nav>

          <div className="mt-6 flex flex-col gap-6 sm:flex-row md:gap-8">
            <div className="shrink-0 sm:w-48 md:w-56">
              <div className="relative overflow-hidden rounded-2xl border border-background-700">
                <img src={comic.cover} alt={`${comic.title}漫画封面`} className="aspect-[3/4] w-full object-cover object-top" />
                <span className="absolute right-2.5 top-2.5 rounded-md bg-background-950/80 px-2 py-1 text-[11px] font-medium text-primary-400">{comic.categoryName}</span>
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded-md px-2.5 py-1 text-xs font-medium ${comic.status === "已完结" ? "bg-accent-500 text-background-950" : "bg-primary-500 text-background-950"}`}>{comic.status}</span>
                {comic.isHot && <span className="flex items-center gap-1 rounded-md bg-accent-500/15 px-2.5 py-1 text-xs font-medium text-accent-400"><i className="ri-fire-fill" />热榜</span>}
              </div>
              <h1 className="mt-3 font-heading text-3xl font-black text-foreground-950 md:text-4xl">{comic.title}</h1>
              <p className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-foreground-500">
                <span className="flex items-center gap-1"><i className="ri-user-line text-primary-400" />{comic.author}</span>
                <span className="flex items-center gap-1"><i className="ri-eye-line text-primary-400" />{comic.views / 10000}万热度</span>
                <span className="flex items-center gap-1"><i className="ri-time-line text-primary-400" />{comic.updatedAt}</span>
              </p>

              <div className="mt-4 flex flex-wrap items-center gap-3">
                <span className="flex items-center gap-1.5 rounded-xl bg-background-900 px-4 py-2">
                  <i className="ri-star-fill text-lg text-primary-400" />
                  <span className="font-heading text-xl font-black text-primary-400">{comic.rating}</span>
                  <span className="text-xs text-foreground-500">读者评分</span>
                </span>
                <button
                  onClick={() => setFollowed((v) => !v)}
                  className={`cursor-pointer whitespace-nowrap rounded-xl px-5 py-2.5 text-sm font-semibold transition-colors ${
                    followed ? "bg-background-800 text-primary-400" : "bg-primary-500 text-background-950 hover:bg-primary-600"
                  }`}
                >
                  <i className={`${followed ? "ri-check-line" : "ri-add-line"} mr-1`} />
                  {followed ? "已追更" : "加入追更"}
                </button>
                <button
                  onClick={() => setReader({ num: comic.chapters[0].num, title: comic.chapters[0].title })}
                  className="cursor-pointer whitespace-nowrap rounded-xl bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-950 transition-colors hover:bg-accent-600"
                >
                  <i className="ri-play-line mr-1" />
                  立即阅读
                </button>
              </div>

              <p className="mt-5 max-w-2xl text-sm leading-7 text-foreground-500 md:text-base">{comic.description}</p>

              <div className="mt-4 flex flex-wrap gap-2">
                {comic.tags.map((t) => (
                  <span key={t} className="rounded-full bg-background-900 px-3 py-1 text-xs text-foreground-600">{t}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 简介 + 章节 */}
      <section className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="rounded-2xl border border-background-800 bg-background-900 p-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-black text-foreground-950">
                <i className="ri-book-2-line text-primary-400" />
                故事简介
              </h2>
              <p className="mt-4 text-sm leading-8 text-foreground-500 md:text-base">{comic.longDescription}</p>
              <div className="mt-6 grid grid-cols-2 gap-4 border-t border-background-800 pt-5 sm:grid-cols-4">
                {[
                  { label: "作者", value: comic.author },
                  { label: "题材", value: comic.categoryName },
                  { label: "状态", value: comic.status },
                  { label: "更新", value: comic.latestChapter },
                ].map((m) => (
                  <div key={m.label}>
                    <p className="text-xs text-foreground-500">{m.label}</p>
                    <p className="mt-1 truncate text-sm font-medium text-foreground-800">{m.value}</p>
                  </div>
                ))}
              </div>
            </div>

            <div id="chapters" className="mt-8">
              <div className="flex items-center justify-between">
                <h2 className="flex items-center gap-2 font-heading text-lg font-black text-foreground-950">
                  <i className="ri-file-list-3-line text-primary-400" />
                  章节列表
                </h2>
                <span className="text-xs text-foreground-500">共 {comic.chapters.length} 章 · 点击章节试读</span>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
                {comic.chapters.map((ch) => (
                  <button
                    key={ch.num}
                    onClick={() => setReader(ch)}
                    className="group flex cursor-pointer items-center justify-between rounded-lg border border-background-800 bg-background-900 px-4 py-3 text-left transition-colors hover:border-primary-500/50 hover:bg-background-800"
                  >
                    <span className="text-sm text-foreground-700 group-hover:text-foreground-950">{ch.title}</span>
                    <span className="shrink-0 text-xs text-foreground-500">{ch.num}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* 侧栏 */}
          <aside className="space-y-6">
            <div className="rounded-2xl border border-background-800 bg-background-900 p-6">
              <h3 className="font-heading text-base font-black text-foreground-950">同题材推荐</h3>
              <div className="mt-4 space-y-3">
                {related.map((c) => (
                  <Link key={c.id} to={`/comic/${c.id}`} className="group flex cursor-pointer items-center gap-3 rounded-lg p-2 transition-colors hover:bg-background-800">
                    <img src={c.cover} alt={`${c.title}封面`} className="h-16 w-12 shrink-0 rounded-md object-cover object-top" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-heading text-sm font-bold text-foreground-800 group-hover:text-primary-300">{c.title}</p>
                      <p className="mt-1 truncate text-xs text-foreground-500">{c.latestChapter}</p>
                      <p className="mt-1 flex items-center gap-1 text-xs text-primary-400"><i className="ri-star-fill text-[10px]" />{c.rating}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-primary-500/30 bg-gradient-to-br from-background-900 to-primary-500/10 p-6">
              <h3 className="font-heading text-base font-black text-foreground-950">追更不错过</h3>
              <p className="mt-2 text-sm leading-6 text-foreground-500">加入追更后，最新章节更新会第一时间提醒你。</p>
              <button
                onClick={() => setFollowed((v) => !v)}
                className="mt-4 w-full cursor-pointer whitespace-nowrap rounded-lg bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-950 transition-colors hover:bg-primary-600"
              >
                {followed ? "✓ 已在追更列表" : "加入追更"}
              </button>
            </div>
          </aside>
        </div>
      </section>

      {/* 相关推荐横排 */}
      {related.length > 0 && (
        <section className="border-t border-background-800 bg-background-900/60">
          <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
            <h2 className="flex items-center gap-2 font-heading text-xl font-black text-foreground-950 md:text-2xl">
              <i className="ri-lightbulb-line text-primary-400" />
              你可能也喜欢
            </h2>
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
              {related.map((c) => (
                <ComicCard key={c.id} comic={c} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 章节试读弹窗 */}
      {reader && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-background-950/80 p-4 backdrop-blur-sm" onClick={() => setReader(null)}>
          <div className="relative flex max-h-[82vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-background-700 bg-background-900" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-background-800 px-6 py-4">
              <div>
                <p className="text-xs text-primary-400">{comic.title}</p>
                <h3 className="mt-1 font-heading text-lg font-bold text-foreground-950">{reader.num} · {reader.title}</h3>
              </div>
              <button onClick={() => setReader(null)} aria-label="关闭" className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg bg-background-800 text-foreground-600 hover:text-foreground-950">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="scrollbar-thin flex-1 overflow-y-auto px-6 py-6 md:px-8">
              <p className="text-base leading-8 text-foreground-600">
                本章为试读内容。在虚空大陆的边际，凛握紧手中那柄泛着金光的剑，目光穿越层层云雾，落向远方盘踞的赤焰巨龙。风掀起他的衣袍，也带来了异世界独有的、混杂着硫磺与青草的气息……
              </p>
              <p className="mt-4 text-base leading-8 text-foreground-600">
                「这一次，我不会再让任何人失去家园。」少年低语，剑锋上流转的光芒愈发炽烈。身后的契约之纹微微发烫，像是回应着他的决心。真正的冒险，才刚刚开始。
              </p>
              <div className="mt-6 flex items-center justify-center gap-2 rounded-xl bg-background-950 py-4 text-xs text-foreground-500">
                <i className="ri-information-line text-primary-400" />
                本章为试读样章，完整内容请持续关注《{comic.title}》更新
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}